import React, { useCallback, useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'

import InformationContainer from '../../components/informationContainer/InformationContainer'
import { useTranslation } from '../../utils/useTranslation'
import { Column, Row } from './common'
import { Switch } from '../form/Switch'
import styled from '@nobia/zeus-components/lib/styled'
import {
  SearchLeadQuery,
  SubscribeToNewsletterMutation,
  UnsubscribeFromNewsletterMutation,
} from '../../graphql/NewsletterQueries'
import PropTypes from 'prop-types'

const NewsletterDescription = styled.span`
  margin-right: auto;
  margin-left: 8px;
`

const Newsletter = ({ profile }) => {
  const { t } = useTranslation('app.pages.user-settings.newsletter')
  const { email, firstName, lastName } = profile || {}

  const { data: leadData } = useQuery(SearchLeadQuery, {
    variables: { email },
    fetchPolicy: 'cache-and-network',
    skip: !email,
  })

  const [subscribeToNewsletter] = useMutation(SubscribeToNewsletterMutation, {
    refetchQueries: ['SearchLead'],
  })

  const [unsubscribeFromNewsletter] = useMutation(
    UnsubscribeFromNewsletterMutation,
    {
      refetchQueries: ['SearchLead'],
    }
  )

  const isSubscribed =
    leadData?.leadCustomer?.searchLead?.isNewsletterSubscribed ?? false
  const [newsletterEnabled, setNewsletterEnabled] = useState(isSubscribed)

  useEffect(() => {
    setNewsletterEnabled(isSubscribed)
  }, [isSubscribed])

  const handleNewsletterChange = useCallback(
    async (checked) => {
      if (!email) return

      setNewsletterEnabled(checked)

      const input = { email, firstName, lastName }
      const mutation = checked
        ? subscribeToNewsletter
        : unsubscribeFromNewsletter

      try {
        await mutation({ variables: { input } })
      } catch (error) {
        setNewsletterEnabled(!checked)
        console.error('Failed to update newsletter subscription:', error)
      }
    },
    [
      email,
      firstName,
      lastName,
      subscribeToNewsletter,
      unsubscribeFromNewsletter,
    ]
  )

  return (
    <InformationContainer title={t('heading')}>
      <Column>
        <Row>
          <NewsletterDescription>{t('description')}</NewsletterDescription>
          <Switch
            id="newsletter"
            checked={newsletterEnabled}
            onChange={handleNewsletterChange}
          />
        </Row>
      </Column>
    </InformationContainer>
  )
}

Newsletter.propTypes = {
  profile: PropTypes.shape({
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
  }).isRequired,
}

export default Newsletter
